import React, { useEffect, useState, useCallback } from 'react';
import { Grid, Tab, Tabs, TextField, Typography, Box } from '@mui/material';
import { StaticDatePicker } from '@mui/x-date-pickers/StaticDatePicker';
import { useSearchParams } from 'react-router-dom';
import { EventGridBookingEditor } from '../../components/EventGrid/EventGridBookingEditor';
import { Event } from '../../services/Types';
import { EditorDetails } from './EditorDetails';
import { EditorDateTime } from './EditorDateTime';
import { Footer } from './Footer';
import { gridColumn, verticalBorders } from '../../global';
import { useEvent } from '../../hooks/useEvent';
import { useToast } from '../../hooks/useToast';
import { ConfirmationModal } from './ConfirmationModal';
import { parseDateParam, formatDateParam, parseNumberParam, updateSearchParams } from '../../utils/urlParams';

// Add TabPanel interface and component
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`detail-view-tabpanel-${index}`}
      aria-labelledby={`detail-view-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `detail-view-tab-${index}`,
    'aria-controls': `detail-view-tabpanel-${index}`,
  };
}


export default function Editor() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { updateDbEvent, getEventData } = useEvent();
  const { showToast, renderToast } = useToast();

  // Initialize state from URL params
  const initialDate = parseDateParam(searchParams.get('date'), null);
  const initialTab = searchParams.get('tab');
  const initialTabValue = initialTab === 'datetime' ? 1 : 0;
  const initialEventId = parseNumberParam(searchParams.get('eventId'), null);

  const [selectedDate, setSelectedDate] = useState<Date | null>(initialDate);
  const [tabValue, setTabValue] = useState(initialTabValue);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [disabled, setDisabled] = useState(true);

  // Confirmation modal state
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  // Update URL params when state changes
  const updateURLParams = useCallback((updates: Record<string, string | number | null | undefined>) => {
    const newParams = updateSearchParams(searchParams, updates);
    setSearchParams(newParams, { replace: true });
  }, [searchParams, setSearchParams]);

  // Load event from URL if eventId is present
  useEffect(() => {
    if (initialEventId && !selectedEvent) {
      getEventData(initialEventId).then((eventData) => {
        if (eventData) {
          setSelectedEvent(eventData);
          // Also set the date from the event if not already set
          if (!selectedDate && eventData.roomBooking?.[0]?.date) {
            const eventDate = new Date(Number(eventData.roomBooking[0].date));
            setSelectedDate(eventDate);
            const newParams = updateSearchParams(searchParams, { date: formatDateParam(eventDate) });
            setSearchParams(newParams, { replace: true });
          }
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  const handleSaveEvent = () => {
    setShowConfirmationModal(true);
  };

  const handleConfirmSave = () => {
    setShowConfirmationModal(false);
    
    selectedEvent && updateDbEvent(selectedEvent, true).then((success) => {
      if (success) {
        showToast(true, 'Event updated successfully', 'success');
      } else {
        showToast(true, 'Error updating event', 'error');
      }
    });
  };

  const handleCancelSave = () => {
    setShowConfirmationModal(false);
  };

  useEffect(() => {
    if (selectedEvent?.eventType &&
      selectedEvent?.title &&
      selectedEvent?.roomBooking?.length > 0 &&
      selectedEvent?.roomBooking[0]?.date &&
      selectedEvent?.roomBooking[0]?.startTime &&
      selectedEvent?.roomBooking[0]?.endTime) {
      setDisabled(false);
    } else {
      setDisabled(true);
    }
  }, [selectedEvent]);

  // Update URL when event is selected
  useEffect(() => {
    if (selectedEvent?.id) {
      updateURLParams({ eventId: selectedEvent.id });
    }
  }, [selectedEvent?.id, updateURLParams]);

  // Update URL when date changes
  const handleDateChange = useCallback((newValue: Date | null) => {
    setSelectedDate(newValue);
    setSelectedEvent(null);
    updateURLParams({ 
      date: newValue ? formatDateParam(newValue) : null,
      eventId: null // Clear eventId when date changes
    });
  }, [updateURLParams]);

  // Update URL when tab changes
  const handleTabChange = useCallback((event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    updateURLParams({ tab: newValue === 0 ? 'details' : 'datetime' });
  }, [updateURLParams]);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={4} sx={{ ...gridColumn, pl: 0 }}>
        <StaticDatePicker
          label="Select date"
          value={selectedDate}
          onChange={handleDateChange}
          renderInput={(params) => <TextField {...params} fullWidth />}
        />
      </Grid>
      <Grid item xs={12} md={4} sx={{...gridColumn, ...verticalBorders, paddingRight: '15px' }}>
        <Typography variant="h6">Select event</Typography>
        {selectedDate && <EventGridBookingEditor searchCriteria={selectedDate} setSelectedEvent={(event) => {
          setSelectedEvent(event);
          if (event?.id) {
            updateURLParams({ eventId: event.id });
          }
        }} />}
      </Grid>
      <Grid item xs={12} md={4} sx={{ ...gridColumn, pr: 0 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          aria-label="Time view mode"
        >
          <Tab label="Details" {...a11yProps(0)} sx={{ minWidth: "49%" }} />
          <Tab label="Date & Time" {...a11yProps(1)} sx={{ minWidth: "49%" }} />
        </Tabs>

        <TabPanel value={tabValue} index={0}>
          {selectedEvent && <EditorDetails event={selectedEvent} setSelectedEvent={setSelectedEvent} />}
        </TabPanel>
        <TabPanel value={tabValue} index={1}>
          {selectedEvent && <EditorDateTime event={selectedEvent} setSelectedEvent={setSelectedEvent} />}
        </TabPanel>
      </Grid>
      <Grid item xs={12}>
        <Footer
          onSave={handleSaveEvent}
          onCancel={() => {window.location.reload();}}
          disabled={disabled}
        />
      </Grid>
      <ConfirmationModal
        open={showConfirmationModal}
        onClose={handleCancelSave}
        onConfirm={handleConfirmSave}
      />
      {renderToast()}
    </Grid>
  );
} 